// Publish-confirmation modal for the banner create/edit dialog.
//
// Triggered by the Publish button — shows the deduped-reach phrase
// from the live audience preview verbatim so the operator confirms
// exactly the same number they just saw.

import React from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { Button } from 'components'
import { BannerRoleT } from 'types/global'
import { formatReachPhrase } from './helpers'

interface PublishConfirmDialogProps {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
  isAllCustomers: boolean
  reach: number
  roles: BannerRoleT[]
  isUpdate?: boolean
}

const PublishConfirmDialog: React.FC<PublishConfirmDialogProps> = ({
  open,
  onCancel,
  onConfirm,
  isAllCustomers,
  reach,
  roles,
  isUpdate = false,
}) => {
  const verb = isUpdate ? 'update' : 'publish'

  // Reach phrase reads "all customers (Admins only) — exact count is
  // role-dependent" / "~3 unique customers" / etc. — see helpers.ts
  // for the full matrix.
  const reachPhrase = formatReachPhrase(reach, roles, isAllCustomers)
  const body = `You're about to ${verb} a banner visible to ${reachPhrase}. Continue?`

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
        Confirm publish
      </DialogTitle>
      <DialogContent>
        <DialogContentText data-testid="publish-confirm-body">
          {body}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="text"
          onClick={onCancel}
          data-cy="publish-confirm-cancel"
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={onConfirm}
          data-cy="publish-confirm-agree"
        >
          {isUpdate ? 'Save changes' : 'Publish banner'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PublishConfirmDialog
